import { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import { Font } from '../../constants'
import { Callback } from '../../modules/callback'
import { Locale } from '../../modules/locale'
import MainModule from '../../modules/main'
import { escapeHtml } from '../../modules/utils'

Locale.getInstance().registerKeySet('fr', 'lvc-regions-field', {
  title: 'regions',
  placeholder: 'Rechercher une region',
  choose: 'Choisir',
  allRegions: 'Toutes les régions',
})

Locale.getInstance().registerKeySet('en', 'lvc-regions-field', {
  title: 'regions',
  placeholder: 'Search for a region',
  choose: 'Choose',
  allRegions: 'All regions',
})

export interface LvcRegionsField {
  title: string
  placeholder: string
  open: boolean
  onChange: string
  defaultValue: any
  value: string
}

export class LvcRegionsField extends MinzeElement {
  private regions: any[] = []
  private selectedIds: string[] = []
  private filter: string = ''
  attrs: Attrs = [
    'title',
    ['chooseLabel', Locale.getKey('lvc-regions-field.choose')],
    'placeholder',
    ['default-value', ''],
    ['on-change', ''],
    ['background', 'transparent'],
    ['border', ''],
    ['dropdownBorder', ''],
    ['error', false],
  ]

  reactive: Reactive = [['value', '']]

  static observedAttributes = ['title', 'on-change', 'default-value', 'error']

  async onBlur(e: Event): Promise<void> {
    this.open = false
    this.rerender()
  }

  async onReady() {
    let geo = MainModule.getInstance().getGeo()

    this.regions = await geo.getAllCounties()
    // add an option to select all regions if that option is not already present
    let allRegionsIndex = this.regions.findIndex(region => region.id === 'allRegions')
    if (allRegionsIndex === -1) {
      this.regions = [
        {
          id: 'allRegions',
          county: Locale.getKey('lvc-regions-field.allRegions'),
        },
        ...this.regions,
      ]
    }
    this.rerender()
  }

  onAttributeChange(
    name?: string | undefined,
    oldValue?: string | null | undefined,
    newValue?: string | null | undefined
  ): void | Promise<void> {
    if (name === 'default-value') {
      this.updateDefaultValue()
    }
  }

  async updateDefaultValue() {
    if (this.defaultValue && this.defaultValue !== true) {
      this.selectedIds = JSON.parse(decodeURIComponent(this.defaultValue)).map(
        (region: any) => `${region.id}`
      )
      this.value = this.defaultValue
    }
    this.rerender()
  }

  async onToggle() {
    this.open = !this.open
    this.rerender()
  }

  onRegionClick(e: Event): void {
    let value = (e.target as HTMLElement).getAttribute('data-id')

    if (!value) {
      return
    }

    if (value === 'allRegions') {
      // Clicked on "allRegions", deselect all other options
      this.selectedIds = [value]
    } else {
      // Clicked on a specific region, it selects it
      let index = this.selectedIds.indexOf(value)

      if (index > -1) {
        this.selectedIds.splice(index, 1)
      } else {
        this.selectedIds.push(value)
      }

      // Check if "allRegions" is selected and deselect it
      let allRegionsIndex = this.selectedIds.indexOf('allRegions')
      if (allRegionsIndex > -1) {
        this.selectedIds.splice(allRegionsIndex, 1)
      }
    }

    this.serializeValue()
    this.rerender()
  }

  async onFilterChange(e: Event) {
    this.filter = (e.target as HTMLInputElement).value
    this.rerender()
  }

  serializeValue() {
    let objects = this.regions.filter(region => this.selectedIds.indexOf(region.id) > -1)
    this.value = encodeURIComponent(JSON.stringify(objects))
    Callback.call(this.onChange, objects)
    this.cast('change', objects)
  }

  getDisciplinesLabel() {
    if (this.selectedIds.length === 0 && !this.chooseLabel) {
      return Locale.getKey('lvc-regions-field.choose')
    } else if (this.selectedIds.length === 0 && this.chooseLabel) {
      return this.chooseLabel
    }

    let labels = this.regions
      .filter(region => this.selectedIds.indexOf(`${region.id}`) > -1)
      .map(region => region.county)

    if (labels.length === 1) {
      return labels[0]
    }

    if (labels.length > 1) {
      return labels[0] + ' + ' + (labels.length - 1)
    }
  }

  renderRegion = (region: any) => {
    if (this.filter && region.county.toLowerCase().indexOf(this.filter.toLowerCase()) === -1) {
      return ''
    }

    let isNotSelected = this.selectedIds.indexOf(`${region.id}`) == -1

    return `
      <button 
        name="region"
        class="${isNotSelected ? 'not-selected' : 'selected'}" 
        data-id="${region.id}">
        ${region.county}
        ${isNotSelected ? '' : `<lvc-circle-check-select-icon data-id="${region.id}"></lvc-circle-check-select-icon>`}
      </button>
    `
  }

  renderRegions = () => {
    let html = ''

    for (let i = 0; i < this.regions.length; i++) {
      html += this.renderRegion(this.regions[i])
    }

    return html
  }

  html = () => {
    let showError = typeof this.error === 'string' && this.error.length > 0
    return `
      <div class="title">${this.title || Locale.getKey('lvc-regions-field.title')}</div>
      <div class="input">
        <button class="dropbtn" name="toggle">
          ${this.getDisciplinesLabel()}
          <lvc-chevron-down-icon></lvc-chevron-down-icon>
        </button>
        <div class="overlay ${this.open ? 'show' : ''}" name="overlay"></div>
        <div class="dropdown-content ${this.open ? 'show' : ''}">
          <div class="header">
            <input name="filter" type="text" value="${escapeHtml(this.filter)}" placeholder="${this.placeholder || Locale.getKey('lvc-regions-field.placeholder')}">
            <lvc-chevron-up-icon></lvc-chevron-up-icon>
          </div>
          <div class="regions" data-selected="${this.selectedIds.length}">
            ${this.renderRegions()}
          </div>
        </div> 
      </div>
    ${showError ? `<lvc-error error="${this.error}"></lvc-error>` : ''}
    `
  }

  css = () => {
    return `
      :host {
      }

      .overlay {
        position: fixed;
        top: 0px;
        left: 0px;
        bottom: 0px;
        right: 0px;
        width: 100%;
        height: 100%;
        z-index: 1;
        display: none;
      }
      .overlay.show {
        display: block;
      }

      .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        height: 48px;
        border: none;
        outline: none;
        padding: 0 16px;
        box-sizing: border-box;
        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        color: var(--Minuit, #1E007D);
        border-top-left-radius: 24px;
        border-top-right-radius: 24px;
        border-bottom: 1px solid var(--Minuit, #1E007D);
        background: var(--Ciel, #BFF0FF);

      }


      input[name="filter"] {
        width: 100%;
        height: 48px;
        border: none;
        outline: none;
        padding: 0 0;
        box-sizing: border-box;
        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        color: var(--Minuit, #1E007D);
        background: transparent;
      }

      .title {
        font-family: inherit;
        color: #1E007D;
        font-size: 14px;
        line-height: 17px;
        font-weight: 700;
        margin-bottom: 8px;
      }

      .input {
        display: flex;
        height: 48px;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        flex-shrink: 0;
        align-self: stretch;
        background: ${this.background ? this.background : 'transparent'};
        position: relative;
        border: ${this.border ? this.border : '1px solid var(--Minuit, #1E007D);'}; 
        border-radius: 24px;
      }

      .regions {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: stretch;
        overflow: hidden;
        overflow-y: auto;
        max-height: calc(300px - 48px);
        padding: 0 10px;
      }


      .regions button {
        display: flex;
        justify-content: space-between;
        font-family: ${Font.family};
        font-size: 14px;
        line-height: 32px;
        cursor: pointer;
        background-color: transparent;
        color: var(--Minuit, #1E007D);
        text-align: left;
        padding: 8px 8px;
        border: none;
        height: 48px;
        flex: 0 0 48px;
        border-bottom: 1px solid rgba(30, 0, 125, 0.30);
      }

      .regions button:last-child {
        border-bottom: none;
      }

      .regions button.selected {
      }


      /* style.css */

      /* Dropdown Button */
      .dropbtn {
        color: white;
        padding: 16px;
        font-size: 16px;
        font-weight: 500;
        border: none;
        cursor: pointer;
        flex: 1;
        width: 100%;
        background-color: transparent;
        color: var(--Minuit, #1E007D);
        text-align: left;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      /* Dropdown button on hover & focus */
      .dropbtn:hover, .dropbtn:focus {
      }

      /* Dropdown Content */
      .dropdown-content {
        width: 100%;
        display: none;
        position: absolute;
        background-color: #f9f9f9;
        min-width: 160px;
        box-shadow: 0px 8px 16px 0px rgba(0,0,0,0.2);
        z-index: 2;
        top: 0;
        border-radius: 24px; 
        max-height: 300px;
        min-height: 96px;
        border: ${this.dropdownBorder ? this.dropdownBorder : '1px solid var(--Minuit, #1E007D);'}; 
        line-height: 32px;
      }

      .dropdown-content.show {
        display: block;
      }


    `
  }

  eventListeners: EventListeners = [
    ['button[name="toggle"]', 'click', this.onToggle.bind(this)],
    ['button[name="region"]', 'click', this.onRegionClick.bind(this)],
    ['input[name="filter"]', 'keyup', this.onFilterChange.bind(this)],
    [this, 'blur', this.onBlur.bind(this)],
    ['div.overlay', 'click', this.onBlur.bind(this)],
  ]
}
